import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from './Header';

const Services = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Global mouse tracking for the parallax effect
  useEffect(() => {
    const handleMouseMove = (e) => {
      setMousePos({
        x: (e.clientX / window.innerWidth) - 0.5,
        y: (e.clientY / window.innerHeight) - 0.5,
      });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="relative overflow-x-hidden bg-[#F0F4F8] text-[#4A5568] antialiased font-sans min-h-screen">
      <Header />

      {/* --- GLOBAL DYNAMIC BACKGROUND (NOW FULL PAGE) --- */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        {/* Grid Overlay */}
        <div className="absolute inset-0 opacity-[0.15]"
             style={{ backgroundImage: `radial-gradient(#2B6CB0 1px, transparent 1px)`, backgroundSize: '40px 40px' }} />

        {/* Floating Interactive Shapes */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={i}
              animate={{
                x: mousePos.x * (i + 1) * 40,
                y: mousePos.y * (i + 1) * 40,
                rotate: mousePos.x * 45,
              }}
              transition={{ type: 'spring', stiffness: 40, damping: 20 }}
              className="absolute border border-[#2B6CB0]/20 rounded-2xl"
              style={{
                width: `${(i + 1) * 250}px`,
                height: `${(i + 1) * 250}px`,
                left: `${20 + i * 20}%`,
                top: `${15 + i * 20}%`,
              }}
            />
          ))}
        </div>
      </div>

      <style dangerouslySetInnerHTML={{ __html: `
        .marquee-text { animation: marquee 40s linear infinite; }
        @keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}} />

      <div className="relative z-10">
        {/* Hero Section - Transparent to show background animation */}
        <section className="relative py-24 bg-transparent border-b border-[#F0F4F8]">
          <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
            <h1 className="text-5xl md:text-8xl font-black mb-6 tracking-tighter text-[#020617] uppercase">
              Our <span className="text-[#2B6CB0]">Services</span>
            </h1>
            <p className="text-xl text-[#4A5568] max-w-3xl mx-auto mb-10 leading-relaxed font-medium">
              A strong online presence is essential to ensure the success of any business. 
              We provide the strategic design and technical precision needed to differentiate your brand.
            </p>
            {/* Primary CTA in Cerulean Blue */}
            <button className="bg-[#2B6CB0] text-white px-10 py-4 rounded-xl font-black uppercase tracking-widest text-sm hover:bg-[#020617] transition-all shadow-lg hover:shadow-[#2B6CB0]/20">
              Explore All Solutions
            </button>
          </div>
        </section>

        <section className="py-24">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            
            {/* 1. Web Design & Development */}
            <div id="website-design" className="mb-32">
              <div className="grid lg:grid-cols-2 gap-16 items-start">
                <div className="order-2 lg:order-1">
                  <Link to="/services/web-design-development" className="block text-4xl md:text-5xl font-black mb-6 text-[#020617] hover:text-[#2B6CB0] transition-colors uppercase tracking-tighter">
                    Web Design & Development
                  </Link>
                  <div className="space-y-4 text-lg leading-relaxed mb-8">
                    <p>
                      Caffryx is a web design agency for teams that want their site to actually move the business, not just look polished. We design and build corporate websites, product marketing experiences, and custom ecommerce that feel clear to visitors and easy to manage for your team.
                    </p>
                  </div>
                  
                  <div className="flex flex-wrap gap-4">
                    <Link to="/services/web-design-development" className="bg-[#2B6CB0] text-white px-8 py-3 rounded-lg font-black uppercase tracking-widest text-xs hover:opacity-90 transition-all">
                      Web Design Service
                    </Link>
                    <Link to="/work" className="border-2 border-[#4A5568] text-[#4A5568] px-8 py-3 rounded-lg font-black uppercase tracking-widest text-xs hover:bg-[#4A5568] hover:text-white transition-all">
                      View Our Work
                    </Link>
                  </div>
                </div>
                <div className="order-1 lg:order-2">
                  <img src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=800" alt="Web Development" className="w-full h-[500px] object-cover rounded-2xl shadow-xl border-8 border-white hover:grayscale-0 transition-all duration-500" />
                </div>
              </div>
            </div>

            {/* 2. Search Engine Optimization (SEO) */}
            <div id="seo" className="mb-32">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="relative">
                  <img src="https://images.unsplash.com/photo-1562577309-2592ab84b1bc?auto=format&fit=crop&q=80&w=800" alt="SEO Strategy" className="w-full h-96 object-cover rounded-2xl shadow-xl border-8 border-white hover:grayscale-0 transition-all duration-500" />
                </div>
                <div>
                  <Link to="/services/seo" className="block text-4xl md:text-5xl font-black mb-6 text-[#020617] hover:text-[#2B6CB0] transition-colors uppercase tracking-tighter">
                    Search Engine Optimization (SEO)
                  </Link>
                  <p className="text-lg mb-8 leading-relaxed">
                    Our SEO services are built to compound, not spike and fade. As a senior led SEO agency inside a broader digital marketing agency, we connect search data with how your buyers actually research and decide. We start with a technical SEO audit, clean crawl and indexing issues, and tune performance.
                  </p>
                  <Link to="/services/seo" className="inline-block bg-[#2B6CB0] text-white px-8 py-3 rounded-lg font-black uppercase tracking-widest text-xs hover:opacity-90 transition-all">
                    See plans
                  </Link>
                </div>
              </div>
            </div>

            {/* 3. Visual Branding & Social Media */}
            <div id="visual-branding" className="mb-32">
              <div className="grid lg:grid-cols-2 gap-16 items-start">
                <div>
                  <Link to="/services/visual-branding" className="block text-4xl md:text-5xl font-black mb-6 text-[#020617] hover:text-[#2B6CB0] transition-colors uppercase tracking-tighter">
                    Visual Branding
                  </Link>
                  <p className="text-lg mb-8 leading-relaxed">
                    Visual branding that makes you recognizable at a glance. We build complete visual identity systems and practical brand guidelines so your team can move fast without losing polish.
                  </p>
                  
                  {/* Subtle UI Container using Aqua Haze background */}
                  <div className="bg-[#F0F4F8] p-6 rounded-2xl border border-[#2B6CB0]/10 mb-8 shadow-sm">
                     <h3 className="text-[#2B6CB0] font-black uppercase tracking-widest text-sm mb-4">Social Media Management</h3>
                     <div className="space-y-3">
                        <div className="flex items-start gap-3">
                          <span className="text-[#2B6CB0] font-bold">→</span>
                          <p className="text-sm font-bold text-[#020617]">Professional Photography & Video Production</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-[#2B6CB0] font-bold">→</span>
                          <p className="text-sm font-bold text-[#020617]">Weekly Posting & Engagement Packages</p>
                        </div>
                        <div className="flex items-start gap-3">
                          <span className="text-[#2B6CB0] font-bold">→</span>
                          <p className="text-sm font-bold text-[#020617]">Content Strategy & High-End Visual Storytelling</p>
                        </div>
                     </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-8">
                    {['Logo Design', 'Graphic Design', 'Typography', 'Brand Style Guide'].map(item => (
                      <div key={item} className="flex items-center gap-2 bg-white p-3 rounded-lg border border-[#F0F4F8] text-xs font-black uppercase tracking-widest shadow-sm text-[#4A5568]">
                        <div className="w-2 h-2 bg-[#2B6CB0] rounded-full"></div> {item}
                      </div>
                    ))}
                  </div>
                  <Link to="/services/visual-branding" className="inline-block bg-[#2B6CB0] text-white px-8 py-3 rounded-lg font-black uppercase tracking-widest text-xs hover:opacity-90 transition-all">
                    Learn more
                  </Link>
                </div>
                <div className="relative">
                  <img src="https://images.unsplash.com/photo-1558655146-9f40138edfeb?auto=format&fit=crop&q=80&w=800" alt="Visual Branding & Design" className="w-full h-[500px] object-cover rounded-2xl shadow-xl border-8 border-white hover:grayscale-0 transition-all duration-500" />
                </div>
              </div>
            </div>

            {/* 4. Brand Research & Strategy */}
            <div id="brand-strategy" className="mb-32">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="relative order-2 lg:order-1">
                  <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&q=80&w=800" alt="Brand Strategy" className="w-full h-96 object-cover rounded-2xl shadow-xl border-8 border-white hover:grayscale-0 transition-all duration-500" />
                </div>
                <div className="order-1 lg:order-2">
                  <Link to="/services/consultation-audit" className="block text-4xl md:text-5xl font-black mb-6 text-[#020617] hover:text-[#2B6CB0] transition-colors uppercase tracking-tighter">
                    Brand Research & Strategy
                  </Link>
                  <p className="text-lg mb-8 leading-relaxed">
                    Brand research and strategy that clarifies why customers choose you. We run stakeholder interviews, customer research, and market analysis to define your ICP and positioning.
                  </p>
                  <div className="grid grid-cols-2 gap-y-3 mb-8">
                    {['Brand Positioning', 'Competitor Analysis', 'Brand Voice', 'Messaging Framework'].map(item => (
                      <div key={item} className="text-xs font-black uppercase tracking-widest flex items-center gap-2 text-[#4A5568]">
                        <span className="text-[#2B6CB0]">✓</span> {item}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* 5. Consultation & Audit */}
            <div id="consultation-audit" className="mb-32">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div>
                  <Link to="/services/consultation-audit" className="block text-4xl md:text-5xl font-black mb-6 text-[#020617] hover:text-[#2B6CB0] transition-colors uppercase tracking-tighter">
                    Consultation & Audit
                  </Link>
                  <p className="text-lg mb-8 leading-relaxed">
                    Senior guidance before you invest. We review your website, analytics, and brand assets to surface high-leverage fixes and a 90-day priority roadmap.
                  </p>
                  <Link to="/services/consultation-audit" className="inline-block bg-[#2B6CB0] text-white px-8 py-3 rounded-lg font-black uppercase tracking-widest text-xs hover:opacity-90 transition-all">
                    Learn more
                  </Link>
                </div>
                <div className="relative">
                  <img src="https://images.unsplash.com/photo-1553484771-371a605b060b?auto=format&fit=crop&q=80&w=800" alt="Consultation & Audit" className="w-full h-96 object-cover rounded-2xl shadow-xl border-8 border-white hover:grayscale-0 transition-all duration-500" />
                </div>
              </div>
            </div>

            {/* 6. User Interface & User Experience */}
            <div id="ui-ux" className="mb-32">
              <div className="grid lg:grid-cols-2 gap-16 items-center">
                <div className="order-2 lg:order-1">
                  <img src="https://images.unsplash.com/photo-1586717791821-3f44a563fa4c?auto=format&fit=crop&q=80&w=800" alt="UI/UX" className="w-full h-96 object-cover rounded-2xl shadow-xl border-8 border-white hover:grayscale-0 transition-all duration-500" />
                </div>
                <div className="order-1 lg:order-2">
                  <Link to="/services/ui-ux" className="block text-4xl md:text-5xl font-black mb-6 text-[#020617] hover:text-[#2B6CB0] transition-colors uppercase tracking-tighter">
                    User Interface & UX
                  </Link>
                  <p className="text-lg mb-8 leading-relaxed">
                    UI/UX design that removes friction and improves conversion. We combine research, prototyping, and usability testing to shape intuitive interfaces.
                  </p>
                  <Link to="/services/ui-ux" className="inline-block bg-[#2B6CB0] text-white px-8 py-3 rounded-lg font-black uppercase tracking-widest text-xs hover:opacity-90 transition-all">
                    Learn more
                  </Link>
                </div>
              </div>
            </div>

            {/* Industries Section */}
            <div className="mb-32">
              <h2 className="text-4xl md:text-6xl font-black text-center mb-16 text-[#020617] uppercase tracking-tighter">Industries We Collaborate With</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {industries.map((item, idx) => (
                  <div key={idx} className="bg-white p-8 rounded-xl border border-[#F0F4F8] shadow-sm hover:shadow-lg hover:border-[#2B6CB0]/30 transition-all group">
                    <h3 className="text-lg font-black mb-4 text-[#020617] uppercase tracking-tighter">{item.title}</h3>
                    <p className="text-[#4A5568] mb-6 text-sm leading-relaxed font-medium">{item.desc}</p>
                    <button className="text-[#2B6CB0] font-black text-[10px] uppercase tracking-widest group-hover:underline">
                      Learn More →
                    </button>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </section>
      </div>
    </div>
  );
};

const industries = [
  { title: "Small Business & Retail", desc: "Main Street's digital storefront. We build high-traffic sites for local shops that turn neighbors into loyal, everyday customers." },
  { title: "Hospitality & Dining", desc: "From local cafes to fine dining. We design immersive menus and booking systems that capture the flavor of our community's favorite spots." },
  { title: "Emerging Entrepreneurs", desc: "Launching the next big idea. We help local founders move from concept to professional brand with high-momentum, conversion-ready sites." },
  { title: "Enterprise & Big Business", desc: "Scalable systems for industry leaders. We build powerful, secure infrastructure that helps our region's largest employers compete globally." },
  { title: "Professional Service Firms", desc: "Where expertise meets clarity. We design trust-first platforms for local consultants, law firms, and agencies to showcase their authority." },
  { title: "Community & Impact", desc: "Building for the common good. We create accessible digital hubs for nonprofits and local organizations to amplify their mission and support." },
];

export default Services;